export interface RefreshPair {
  accessToken: string;
  refreshToken: string;
}
interface RefreshOptions {
  /** API/session namespace; never put credentials in the lock name. */
  scope: string;
  refreshToken: string;
  /** Must read persisted storage, not a tab's stale in-memory store. */
  read: () => RefreshPair | null;
  request: (refreshToken: string) => Promise<RefreshPair>;
  /** Persist inside the lock so the next tab can observe the result. */
  persist: (pair: RefreshPair) => void;
}

/** Separate instances model separate tabs; the browser lock is origin-wide. */
export function createRefreshCoordinator() {
  const flights = new Map<string, Promise<RefreshPair>>();
  return function coordinate(options: RefreshOptions): Promise<RefreshPair> {
    const key = `${options.scope}\n${options.refreshToken}`;
    const existing = flights.get(key);
    if (existing) return existing;
    const work = async (): Promise<RefreshPair> => {
      const current = options.read();
      if (!current) throw new Error('Session ended');
      if (current.refreshToken !== options.refreshToken && current.accessToken) return current;
      const pair = await options.request(current.refreshToken);
      if (!pair.accessToken || !pair.refreshToken) throw new Error('Invalid refresh response');
      // A logout/login while the request was pending must not resurrect a session.
      const latest = options.read();
      if (latest?.refreshToken !== current.refreshToken) {
        // Without Web Locks, the server can return the exact pair already saved by a sibling.
        if (latest?.refreshToken === pair.refreshToken) return latest;
        throw new Error('Session changed');
      }
      options.persist(pair);
      return pair;
    };
    const promise = Promise.resolve().then(async (): Promise<RefreshPair> =>
      typeof navigator !== 'undefined' && navigator.locks
        ? await navigator.locks.request(`oprus:refresh:${options.scope}`, work)
        : await work(),
    ).finally(() => { flights.delete(key); });
    flights.set(key, promise);
    return promise;
  };
}

// Symbol.for also shares flights if two bundles load separate package copies.
const coordinatorKey = Symbol.for('oprus.auth.refreshCoordinator');
const shared = globalThis as typeof globalThis & { [coordinatorKey]?: ReturnType<typeof createRefreshCoordinator> };
export const coordinateRefresh = shared[coordinatorKey] ??= createRefreshCoordinator();

/** Read Zustand persistence without mutating/rehydrating an entire stale store. */
export function readPersistedRefreshPair(key: string, fallback: () => RefreshPair | null): RefreshPair | null {
  if (typeof localStorage === 'undefined') return fallback();
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  const value: unknown = JSON.parse(raw);
  if (!value || typeof value !== 'object' || !('state' in value)) throw new Error('Invalid session storage');
  if (!value.state || typeof value.state !== 'object') throw new Error('Invalid session storage');
  const state = value.state as Record<string, unknown>;
  const accessToken = state.token ?? state.accessToken;
  const refreshToken = state.refreshTokenValue ?? state.refreshToken;
  return typeof refreshToken === 'string' && refreshToken
    ? { accessToken: typeof accessToken === 'string' ? accessToken : '', refreshToken } : null;
}

const accessFlightsKey = Symbol.for('oprus.auth.accessRefreshFlights');
const accessShared = globalThis as typeof globalThis & { [accessFlightsKey]?: Map<string, Promise<string | null>> };
const accessFlights = accessShared[accessFlightsKey] ??= new Map<string, Promise<string | null>>();

/** Proactive and reactive interceptors on every instance use the same flight. */
export async function singleFlightAccessRefresh(
  scope: string, attempted: string, refresh: () => Promise<string | null>,
  read: () => string | null | Promise<string | null>,
): Promise<string | null> {
  const current = await read();
  if (current && current !== attempted) return current;
  const key = `${scope}\n${attempted}`;
  let flight = accessFlights.get(key);
  if (!flight) {
    flight = Promise.resolve().then(refresh).finally(() => { accessFlights.delete(key); });
    accessFlights.set(key, flight);
  }
  return flight;
}
